import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  TrendingDown,
  Percent,
  PoundSterling,
  Calendar,
  BarChart3,
  ArrowRight,
} from 'lucide-react';
import { LucideIcon } from 'lucide-react';

interface ReportCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  href: string;
}

function ReportCard({ title, description, icon: Icon, href }: ReportCardProps) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="rounded-lg bg-primary/10 p-2">
            <Icon className="h-6 w-6 text-primary" />
          </div>
        </div>
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Link to={href}>
          <Button variant="outline" className="w-full">
            View Report
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

export default function ReportsDashboard() {
  const reports: ReportCardProps[] = [
    {
      title: 'Out of Stock',
      description: 'Track OOS duration by product and location',
      icon: TrendingDown,
      href: '/reports/out-of-stock',
    },
    {
      title: 'Margin Analysis',
      description: 'Products below target margin',
      icon: Percent,
      href: '/reports/margins',
    },
    {
      title: 'Stock Valuation',
      description: 'Inventory value by location',
      icon: PoundSterling,
      href: '/reports/stock-value',
    },
    {
      title: 'Days of Cover',
      description: 'Stock runway by product',
      icon: Calendar,
      href: '/reports/days-of-cover',
    },
    {
      title: 'Sales Report',
      description: 'Sales by product, machine, period',
      icon: BarChart3,
      href: '/reports/sales',
    },
  ];

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold">Reports</h1>
          <p className="text-muted-foreground">Access operational and financial reports</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map((report) => (
            <ReportCard key={report.href} {...report} />
          ))}
        </div>
      </div>
    </div>
  );
}
